/**
 * AdminFoodItems — super-admin CRUD for the Food Pulse Global Food
 * Knowledge Database.
 *
 * Route: /admin/food-items (admin-only)
 *
 * Features:
 *   - Search + category filter of existing items
 *   - Trigger AI generation for a single food (queued via
 *     POST /api/food-items/generate)
 *   - Toggle featured flag
 *   - Delete
 *   - Deep-link into the public detail page
 *
 * Manual field-editing of AI-generated profiles is intentionally out
 * of scope for Phase 1 — admins can regenerate to fix mistakes and can
 * edit the raw JSON directly through the shell if truly needed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Star, Trash2, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const t = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminFoodItems() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genName, setGenName] = useState("");
  const [genCat, setGenCat] = useState("");
  const [overwrite, setOverwrite] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/food-items`, { params: { q: q || undefined, category: cat || undefined, limit: 200 } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const runGenerate = async () => {
    if (!genName.trim()) { toast.error("Please enter a food name"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/api/food-items/generate`,
        { name: genName.trim(), category: genCat || undefined, overwrite },
        { headers: authHeader() },
      );
      toast.success(`Generated: ${res.data.common_name}`);
      setGenName("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const toggleFeatured = async (f) => {
    try {
      await axios.patch(`${API}/api/food-items/${f.slug}`, { common_name: f.common_name, featured: !f.featured }, { headers: authHeader() });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete ${f.common_name}?`)) return;
    try {
      await axios.delete(`${API}/api/food-items/${f.slug}`, { headers: authHeader() });
      toast.success(`Deleted ${f.common_name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-food-items">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl">Food Pulse — Global Food Database</h1>
          <div className="text-sm opacity-70">{total.toLocaleString()} profile{total === 1 ? "" : "s"} in database</div>
        </div>
        <Link to="/food-pulse/food" className="text-sm underline">Public directory ↗</Link>
      </div>

      {/* Generate panel */}
      <div className="rounded-2xl border p-5 bg-white mb-6" style={{ borderColor: "#B8D3A1" }} data-testid="admin-food-generate-panel">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#2F6B2F" }}>
          <Sparkles className="w-3 h-3 inline mr-1" /> AI generation (Claude Sonnet 4.6)
        </div>
        <h2 className="font-display text-lg mb-3">Generate a food-item profile</h2>
        <div className="grid sm:grid-cols-[minmax(0,1fr)_180px_120px_auto] gap-2">
          <input
            type="text"
            placeholder="Food name (e.g. Alphonso Mango)"
            value={genName}
            onChange={(e) => setGenName(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "#B8D3A1" }}
            data-testid="admin-food-gen-name"
          />
          <input
            type="text"
            placeholder="Category hint (optional)"
            value={genCat}
            onChange={(e) => setGenCat(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "#B8D3A1" }}
            data-testid="admin-food-gen-cat"
          />
          <label className="text-xs inline-flex items-center gap-1.5">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="admin-food-gen-overwrite" />
            Overwrite
          </label>
          <button
            type="button"
            onClick={runGenerate}
            disabled={generating}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center gap-2"
            style={{ background: "#2F6B2F" }}
            data-testid="admin-food-gen-submit"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : "Generate profile"}
          </button>
        </div>
        <div className="text-xs opacity-70 mt-2">
          The generation call may take up to a minute — a full 22-section profile with 25+ recipes is being written.
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input
          type="search"
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          className="rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "#B8D3A1" }}
          data-testid="admin-food-search"
        />
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "#B8D3A1" }}
          data-testid="admin-food-cat-filter"
        >
          <option value="">All categories</option>
          {["fruits","vegetables","leafy-greens","herbs","spices","grains","rice","legumes","nuts","seeds","seafood","meat","poultry","dairy","eggs","fermented","beverages","other"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button type="button" onClick={load} className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "#B8D3A1" }} data-testid="admin-food-refresh">Refresh</button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#B8D3A1" }}>
        {loading ? (
          <div className="p-8 text-center text-sm opacity-70">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm opacity-70">No profiles yet. Use the generator above to create the first one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead style={{ background: "#DCEAD1" }}>
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Featured</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.slug} className="border-t" style={{ borderColor: "#DCEAD1" }} data-testid={`admin-food-row-${f.slug}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{f.common_name}</div>
                    {f.scientific_name && <div className="text-xs italic opacity-70">{f.scientific_name}</div>}
                    <div className="text-[10px] font-mono opacity-50">/{f.slug}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{f.category}</td>
                  <td className="px-3 py-2 text-xs">{f.source || "—"}</td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => toggleFeatured(f)} className="inline-flex" data-testid={`admin-food-star-${f.slug}`}>
                      <Star className="w-4 h-4" fill={f.featured ? "#F0B429" : "none"} stroke={f.featured ? "#F0B429" : "currentColor"} />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Link to={`/food-pulse/food/${f.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs underline mr-3" data-testid={`admin-food-view-${f.slug}`}>
                      View <ExternalLink className="w-3 h-3" />
                    </Link>
                    <button type="button" onClick={() => del(f)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`admin-food-del-${f.slug}`}>
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
